import { useQuery } from "@tanstack/react-query";
import { xano, type ProviderMapping } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { EngineView } from "@/components/EngineView";
import { StatCard, SectionTitle, DataTable } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Plug } from "lucide-react";
import { gbp, num } from "@/lib/format";
import type { AccountingMapping } from "@/lib/engine-types";

function ProviderMappingSection() {
  const { activeToken } = useCompany();
  const q = useQuery({
    queryKey: ["provider-mapping", activeToken],
    queryFn: () => xano.accounting.mapping(activeToken!),
    enabled: !!activeToken,
  });
  const d = q.data as ProviderMapping | undefined;
  if (!d || !d.provider) return null;
  const name = d.provider.charAt(0).toUpperCase() + d.provider.slice(1);
  return (
    <>
      <SectionTitle>How your orders post into {name}</SectionTitle>
      <div className="-mt-2 mb-3 flex items-center gap-2 text-sm">
        {d.connected ? (
          <Badge variant="success">
            <CheckCircle2 className="h-3 w-3" /> {name} connected
          </Badge>
        ) : (
          <Badge variant="secondary">
            <Plug className="h-3 w-3" /> {name} not connected
          </Badge>
        )}
        <span className="text-muted-foreground">
          Default mapping. goforma tunes these per account before going live.
        </span>
      </div>
      <DataTable
        columns={[
          { key: "treatment", label: "Treatment" },
          { key: "sales", label: "Sales account" },
          { key: "salestax", label: "Sales tax code" },
          { key: "vat", label: "VAT account" },
        ]}
        rows={(d.mappings ?? []).map((m) => ({
          treatment: <span className="font-medium">{m.label}</span>,
          sales: <Badge variant="secondary">{m.sales_account_code}</Badge>,
          salestax: <Badge variant="secondary">{m.sales_tax_type}</Badge>,
          vat: m.vat_account_code ? <Badge variant="secondary">{m.vat_account_code}</Badge> : "—",
        }))}
      />
    </>
  );
}

function render(raw: unknown) {
  const d = raw as AccountingMapping;
  const j = d.sample_journal;
  const totalDebit = j?.lines?.reduce((s, l) => s + (l.debit || 0), 0) ?? 0;
  const totalCredit = j?.lines?.reduce((s, l) => s + (l.credit || 0), 0) ?? 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Orders classified" value={num(d.order_count)} />
        <StatCard label="VAT treatments" value={num(d.treatments?.length)} hint="Distinct postings" />
        <StatCard
          label="Miscoded"
          value={num(d.miscoded?.count)}
          tone={d.miscoded?.count > 0 ? "destructive" : "success"}
          hint={d.miscoded?.count > 0 ? "Need correction journals" : "All clean"}
        />
      </div>

      <SectionTitle>How your orders post</SectionTitle>
      <DataTable
        columns={[
          { key: "label", label: "Treatment" },
          { key: "code", label: "Tax code" },
          { key: "orders", label: "Orders", align: "right" },
          { key: "net", label: "Net sales", align: "right" },
          { key: "vat", label: "VAT", align: "right" },
        ]}
        rows={(d.treatments ?? []).map((t) => ({
          label: (
            <div>
              <div className="font-medium">{t.label}</div>
              <div className="text-xs text-muted-foreground">{t.treatment}</div>
            </div>
          ),
          code: <Badge variant="secondary">{t.qb_tax_code}</Badge>,
          orders: num(t.order_count),
          net: gbp(t.net_sales, { decimals: true }),
          vat: gbp(t.vat, { decimals: true }),
        }))}
      />

      {j && (
        <>
          <SectionTitle>Sample journal</SectionTitle>
          <Card>
            <CardContent className="p-5">
              <div className="mb-3 flex items-center gap-2 text-sm">
                <span className="font-medium">Order {j.order}</span>
                <Badge variant="secondary">{j.country}</Badge>
                <span className="text-muted-foreground">goods {gbp(j.goods, { decimals: true })}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 text-left font-medium">Account</th>
                    <th className="py-2 text-right font-medium">Debit</th>
                    <th className="py-2 text-right font-medium">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {j.lines.map((l, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{l.account}</td>
                      <td className="py-2 text-right tabular-nums">
                        {l.debit ? gbp(l.debit, { decimals: true }) : ""}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {l.credit ? gbp(l.credit, { decimals: true }) : ""}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-medium">
                    <td className="py-2">Total</td>
                    <td className="py-2 text-right tabular-nums">{gbp(totalDebit, { decimals: true })}</td>
                    <td className="py-2 text-right tabular-nums">{gbp(totalCredit, { decimals: true })}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      <ProviderMappingSection />
    </>
  );
}

export default function Books() {
  return (
    <EngineView
      title="Books"
      description="How each order posts: VAT treatment, provider tax code and the double-entry journal."
      queryKey="accounting-mapping"
      queryFn={(token) => xano.dashboard.accountingMapping(token)}
      render={render}
    />
  );
}
